import { apiErrorField, customFetch } from '@agos/api-client'
import {
  getGetInvoicesQueryKey,
  useGetInvoices,
  useGetInvoicesStats,
  useGetInvoicesInvoiceId,
  usePatchInvoicesInvoiceId,
  usePostInvoices,
  usePostInvoicesInvoiceIdApprove,
  usePostInvoicesInvoiceIdConfirm,
  usePostInvoicesInvoiceIdMarkPaid,
  usePostInvoicesInvoiceIdMarkUnpaid,
  usePostInvoicesParsePdf,
  usePutInvoicesInvoiceIdLines,
} from '@agos/api-client/invoicing'
import {
  getSuppliersSupplierIdResolveCodeCode,
  useGetStockItems,
  useGetSuppliers,
} from '@agos/api-client/inventory'
import {
  useGetInvoicesInvoiceIdPaymentAttempts,
  useGetTatrabankaSettings,
  usePostInvoicesInvoiceIdWithdrawFromBatch,
  usePostInvoicesSepaBatch,
} from '@agos/api-client/payments'
import { T, downloadTextFile, rateOf, tt, useAuth } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  BadgeEuro,
  Banknote,
  CheckCircle2,
  Clock,
  Eye,
  FileDown,
  FileText,
  Paperclip,
  Pencil,
  Plus,
  Receipt,
  Undo2,
  Upload,
  X,
} from 'lucide-react'
import { useMemo, useState, type DragEvent, type ReactNode } from 'react'

import {
  attemptLabel,
  blockedByApproval,
  byAttention,
  byMostRecent,
  canConfirm,
  canSave,
  daysOverdue,
  daysUntilDue,
  defaultExecutionDate,
  emptyLineForm,
  filterInvoices,
  formDelta,
  formProblems,
  formsFromParse,
  formatCents,
  headerAddsUp,
  hasFormLines,
  hasLines,
  canWithdrawFromBatch,
  amountsEditable,
  headerDelta,
  identifiersEditable,
  headerFormOf,
  headerPayload,
  headerTotals,
  isMixedRate,
  lineFormOf,
  lineNetDriftCents,
  lineOf,
  linePayloads,
  lineSum,
  bookTotalsOf,
  filteredNote,
  listTotals,
  looksLikeSepaXml,
  needsApproval,
  needsReview,
  parseModeLabel,
  parseStamp,
  parseWarnings,
  payableIds,
  payeeIbanValid,
  paymentCountIn,
  pdfsOnly,
  planBatch,
  reviewCount,
  senderProblem,
  sepaFileName,
  sepaSkipReason,
  withdrawReasonAccepted,
  withdrawWarning,
  statedLineTotals,
  statusColor,
  statusLabel,
  confirmOutcomeMessage,
  confirmOutcomeOf,
  stockImpact,
  stockImpactNote,
  totalsFromLines,
  unresolvedCodes,
  vatBands,
  wholeBaseExplainsDelta,
  withEdit,
  type FormProblem,
  type HeaderForm,
  type InvoiceDetail,
  type InvoiceRow,
  type LineForm,
  type ParsedInvoice,
  type SepaSkipReason,
} from './supplier-invoice'
import {
  attachmentUrl,
  emptyNote,
  endpointFor,
  fieldNameFor,
  fileProblem,
  hasAttachment,
  noDeleteNote,
  replaceWarning,
} from './attachments'

/**
 * `/invoices` — the invoices suppliers send *us*. What gets paid out of the bank.
 *
 * **FB-090.** Legacy's form recomputes the header out of the lines and posts it on every
 * save. On an outgoing invoice that is right; here it silently rewrites what a supplier
 * asked for. TRADE TECH's `20260364` moves from 137,86 € to 137,87 € on a save that only
 * touched its constant symbol, and `AutoPayService.buildSepaBatch` pays the header. So the
 * header is the supplier's figure, the difference against the lines is named on the screen,
 * and replacing it is a button.
 *
 * **FB-091.** Confirming an invoice used to receive the goods — a stock receipt, a movement
 * per mapped line, `stock_items.qty` and `cost_price` refreshed, the supplier's item codes
 * learned. `InvoiceService.confirm` sets a status. The gap is stated rather than papered
 * over, and filed against the backend.
 *
 * **FB-092.** The SEPA batch is the payment path that works, and it cannot build a file
 * until the sender IBAN is set — which it is not. Legacy shows the button and finds out
 * after the click. Downloading also *writes*: a `queued` payment attempt per invoice, which
 * is what stops the same invoice going into two batches.
 *
 * Ported from legacy `pages/invoices.tsx` (1 266 lines).
 */

interface SupplierOption {
  id?: number
  name?: string
  iban?: string | null
  defaultPaymentForm?: string | null
  defaultConstantSymbol?: string | null
}

interface StockItemOption {
  id?: number
  name?: string
  unit?: string
}

interface Listing {
  threshold?: number | string | null
  rows?: InvoiceRow[]
}

export function InvoicesPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user } = useAuth()
  const isMainAdmin = user?.systemRole === 'main_admin'
  const [now] = useState(() => Date.now())

  const [status, setStatus] = useState('all')
  const [supplierFilter, setSupplierFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [viewingId, setViewingId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [prefill, setPrefill] = useState<{ header: HeaderForm; lines: LineForm[]; parsed: ParsedInvoice } | null>(null)
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set())
  const [confirming, setConfirming] = useState<InvoiceRow | null>(null)
  const [approving, setApproving] = useState<InvoiceRow | null>(null)
  const [marking, setMarking] = useState<InvoiceRow | null>(null)
  const [unmarking, setUnmarking] = useState<InvoiceRow | null>(null)
  // §9.76 — the invoice being taken back out of a batch, and the reason that must be typed first.
  const [withdrawing, setWithdrawing] = useState<InvoiceRow | null>(null)
  const [withdrawReason, setWithdrawReason] = useState('')
  const [batching, setBatching] = useState(false)
  const [dragging, setDragging] = useState(false)

  const { data, isLoading } = useGetInvoices({
    ...(status === 'all' ? {} : { status }),
    ...(supplierFilter === 'all' ? {} : { supplierId: Number(supplierFilter) }),
  })
  const listing = (data ?? {}) as Listing
  const invoices = useMemo<InvoiceRow[]>(() => listing.rows ?? [], [listing.rows])
  // The threshold travels with the list so a row cannot render before the rule it is judged
  // by. In euros on the wire; compared in cents here.
  // `null` when nobody has configured a gate, which is not the same as a gate at zero — FB-135. The
  // server answered `0` for both until 2026-08-02, so this used to collapse them with `?? 0`.
  const thresholdCents =
    listing.threshold === null || listing.threshold === undefined
      ? null
      : Math.round(Number(listing.threshold) * 100)

  const { data: supplierData } = useGetSuppliers({ active: 'true' })
  const suppliers = useMemo<SupplierOption[]>(() => (supplierData ?? []), [supplierData])
  const { data: bankData } = useGetTatrabankaSettings()
  const sender = senderProblem(bankData)

  const shown = useMemo(
    () => byAttention(filterInvoices(invoices, { status, supplierId: supplierFilter, search }), now),
    [invoices, status, supplierFilter, search, now],
  )
  const totals = listTotals(invoices, now)
  // §9.256 — the tiles are a statement about the books, and `useGetInvoices` sends the status and the
  // supplier to the server, so summing what came back made *Nezaplatené* read nought under a
  // *Zaplatené* filter. `GET /invoices-stats` answers the unconditional figures and nothing read it.
  const { data: statsData } = useGetInvoicesStats()
  const books = bookTotalsOf(statsData)
  const filtered = status !== 'all' || supplierFilter !== 'all'
  const plan = planBatch(invoices, selected)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetInvoicesQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Operácia sa nepodarila.'),
      variant: 'destructive',
    })

  const confirm = usePostInvoicesInvoiceIdConfirm({
    mutation: {
      // FB-091. The confirm reports what it did — how many lines it mapped, how many it learned, how
      // many it could not place and whether the delivery had already been received. Said out loud,
      // because an unmapped line is work somebody has to go and do and the screen is the only place
      // that knows.
      onSuccess: (result) => {
        setConfirming(null)
        invalidate()
        toast({
          title: tt('Faktúra potvrdená'),
          description: confirmOutcomeMessage(confirmOutcomeOf(result)),
        })
      },
      onError,
    },
  })
  const withdraw = usePostInvoicesInvoiceIdWithdrawFromBatch({
    mutation: {
      onSuccess: () => {
        setWithdrawing(null)
        setWithdrawReason('')
        invalidate()
      },
      onError,
    },
  })
  const approve = usePostInvoicesInvoiceIdApprove({
    mutation: {
      onSuccess: () => {
        setApproving(null)
        invalidate()
      },
      onError,
    },
  })
  const markPaid = usePostInvoicesInvoiceIdMarkPaid({
    mutation: {
      onSuccess: () => {
        setMarking(null)
        invalidate()
      },
      onError,
    },
  })
  const markUnpaid = usePostInvoicesInvoiceIdMarkUnpaid({
    mutation: {
      onSuccess: () => {
        setUnmarking(null)
        invalidate()
      },
      onError,
    },
  })

  /**
   * The batch download. It writes — a `queued` attempt per payment — so it is behind a
   * confirmation, and what came back is checked before it is saved to disk: a payment file
   * that downloaded as an HTML error page reaches the bank's upload form before anybody
   * reads it.
   */
  const sepaBatch = usePostInvoicesSepaBatch({
    mutation: {
      onSuccess: (xml: string) => {
        setBatching(false)
        if (!looksLikeSepaXml(xml)) {
          toast({
            title: tt('Odpoveď nie je platný SEPA súbor'),
            description: tt('Nič sa neuložilo. Skontrolujte nastavenia banky.'),
            variant: 'destructive',
          })
          return
        }
        const executionDate = defaultExecutionDate(now)
        downloadTextFile(sepaFileName(executionDate), xml, 'application/xml')
        setSelected(new Set())
        invalidate()
        toast({
          title: tt('SEPA dávka pripravená'),
          description: `${paymentCountIn(xml)} ${tt('platieb v súbore. Nahrajte ho do internet bankingu a tam ho schváľte - samo sa nič neodošle.')}`,
        })
      },
      onError: (error: unknown) => {
        setBatching(false)
        onError(error)
      },
    },
  })

  const parsePdf = usePostInvoicesParsePdf({
    mutation: {
      onSuccess: (result: unknown) => {
        const parsed = (result ?? {}) as ParsedInvoice
        const forms = formsFromParse(parsed)
        setPrefill({ ...forms, parsed })
        setCreating(true)
      },
      onError: (error: unknown) =>
        toast({
          title: tt('PDF sa nepodarilo prečítať'),
          description: messageOf(error) ?? tt('Zadajte faktúru ručne.'),
          variant: 'destructive',
        }),
    },
  })

  const uploadPdf = (files: File[]) => {
    const pdfs = pdfsOnly(files)
    if (pdfs.length === 0) {
      toast({ title: tt('Iba PDF súbory'), variant: 'destructive' })
      return
    }
    if (pdfs.length > 1) {
      // Legacy parses them all at once and queues the drafts. One at a time here: each
      // draft carries a total that has to be checked against the paper, and a queue of five
      // half-checked tax documents is how the checking stops happening.
      toast({
        title: tt('Naraz jedna faktúra'),
        description: tt('Každú treba porovnať s papierom, tak sa otvorí prvá.'),
      })
    }
    const first = pdfs[0]
    if (first) parsePdf.mutate({ data: { pdf: first } })
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    uploadPdf([...event.dataTransfer.files])
  }

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const columns: Column<InvoiceRow>[] = useMemo(
    () => [
      {
        key: 'select',
        header: '',
        render: (invoice) => {
          const reason = sepaSkipReason(invoice)
          return (
            <div className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={invoice.id !== undefined && selected.has(invoice.id)}
                disabled={reason !== null}
                onChange={() => invoice.id !== undefined && toggle(invoice.id)}
                aria-label={
                  reason === null
                    ? `${tt('Zaradiť do SEPA dávky')} ${invoice.invoiceNumber ?? ''}`
                    : `${invoice.invoiceNumber ?? ''} - ${skipReasonText(reason)}`
                }
                title={reason === null ? tt('Zaradiť do SEPA dávky') : skipReasonText(reason)}
                className="w-4 h-4 rounded accent-primary"
              />
              {/* §9.76. Beside the reason it is refused, because that is where somebody is standing
                  when they find the invoice they wanted is not selectable - not a click away in the
                  detail panel. Only for `queued`: the other three skip reasons have nothing to
                  withdraw from. */}
              {canWithdrawFromBatch(invoice) && invoice.id !== undefined && (
                <IconButton
                  label={tt('Stiahnuť z dávky')}
                  hint={tt('Len ak sa dávka nikdy nenahrala do banky')}
                  tone="amber"
                  onClick={() => {
                    setWithdrawing(invoice)
                    setWithdrawReason('')
                  }}
                >
                  <Undo2 className="w-3.5 h-3.5" data-withdraw-from-batch={invoice.id} />
                </IconButton>
              )}
            </div>
          )
        },
      },
      {
        key: 'invoiceNumber',
        header: tt('Číslo'),
        sortable: true,
        sortValue: (invoice) => invoice.invoiceNumber ?? '',
        render: (invoice) => (
          <div className="min-w-0">
            <p className="font-mono text-xs font-bold text-primary">{invoice.invoiceNumber}</p>
            {invoice.variableSymbol && invoice.variableSymbol !== invoice.invoiceNumber && (
              <p className="text-[11px] text-muted-foreground">VS {invoice.variableSymbol}</p>
            )}
          </div>
        ),
      },
      {
        key: 'supplierName',
        header: tt('Dodávateľ'),
        sortable: true,
        sortValue: (invoice) => invoice.supplierName ?? '',
        render: (invoice) => (
          <span className="text-sm font-semibold truncate">
            {invoice.supplierName ?? `#${invoice.supplierId ?? ''}`}
          </span>
        ),
      },
      {
        key: 'dueDate',
        header: tt('Splatnosť'),
        sortable: true,
        sortValue: (invoice) => invoice.dueDate ?? '',
        render: (invoice) => {
          const late = daysOverdue(invoice, now)
          const left = daysUntilDue(invoice, now)
          return (
            <span className="text-xs whitespace-nowrap">
              {invoice.dueDate ?? '-'}
              {late !== null && (
                <span className="block text-[11px] font-semibold text-red-500">
                  {late} {tt('dní po termíne')}
                </span>
              )}
              {left !== null && (
                <span className="block text-[11px] text-muted-foreground">
                  {tt('ostáva')} {left} {tt('dní')}
                </span>
              )}
            </span>
          )
        },
      },
      {
        key: 'totalGross',
        header: tt('Suma'),
        sortable: true,
        sortValue: (invoice) => headerTotals(invoice).grossCents,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (invoice) => (
          <div className="text-right whitespace-nowrap">
            <span className="font-bold tabular-nums">
              {formatCents(headerTotals(invoice).grossCents)}
            </span>
            {!headerAddsUp(invoice) && (
              <span
                className="block text-[11px] font-semibold text-amber-500"
                data-header-suspect
                title={tt('Základ plus DPH nedáva túto sumu')}
              >
                <T>súčty nesúhlasia</T>
              </span>
            )}
            {needsApproval(invoice, thresholdCents) && (
              <span className="block text-[11px] text-amber-500">
                {invoice.approvedAt === null || invoice.approvedAt === undefined ? (
                  <T>čaká na schválenie</T>
                ) : (
                  <T>schválená</T>
                )}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: tt('Stav'),
        sortable: true,
        sortValue: (invoice) => invoice.status ?? '',
        render: (invoice) => (
          <StatusBadge label={statusLabel(invoice.status)} color={statusColor(invoice.status)} />
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (invoice) => (
          <div className="flex items-center gap-1 justify-end">
            <IconButton
              label={`${tt('Otvoriť faktúru')} ${invoice.invoiceNumber ?? ''}`}
              onClick={() => setViewingId(invoice.id ?? null)}
            >
              <Eye className="w-3.5 h-3.5" />
            </IconButton>
            {identifiersEditable(invoice) && (
              <IconButton
                label={`${tt('Upraviť faktúru')} ${invoice.invoiceNumber ?? ''}`}
                onClick={() => setEditingId(invoice.id ?? null)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </IconButton>
            )}
            {blockedByApproval(invoice, thresholdCents) ? (
              isMainAdmin ? (
                <IconButton
                  label={`${tt('Schváliť faktúru')} ${invoice.invoiceNumber ?? ''}`}
                  tone="amber"
                  onClick={() => setApproving(invoice)}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                </IconButton>
              ) : (
                <span
                  className="p-1.5 text-amber-500/60"
                  title={tt('Vyžaduje schválenie hlavného administrátora')}
                  data-approval-locked
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                </span>
              )
            ) : null}
            {canConfirm(invoice, thresholdCents) && (
              <IconButton
                label={`${tt('Potvrdiť faktúru')} ${invoice.invoiceNumber ?? ''}`}
                onClick={() => setConfirming(invoice)}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
              </IconButton>
            )}
            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
              <IconButton
                label={`${tt('Označiť ako zaplatenú')} ${invoice.invoiceNumber ?? ''}`}
                onClick={() => setMarking(invoice)}
              >
                <Banknote className="w-3.5 h-3.5" />
              </IconButton>
            )}
            {invoice.status === 'paid' && (
              <IconButton
                label={`${tt('Vrátiť na nezaplatenú')} ${invoice.invoiceNumber ?? ''}`}
                tone="amber"
                onClick={() => setUnmarking(invoice)}
              >
                <Undo2 className="w-3.5 h-3.5" />
              </IconButton>
            )}
          </div>
        ),
      },
    ],
    [now, selected, thresholdCents, isMainAdmin],
  )

  return (
    <div
      className="space-y-5"
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes('Files')) {
          event.preventDefault()
          setDragging(true)
        }
      }}
      onDragLeave={(event) => {
        const next = event.relatedTarget as Node | null
        if (!next || !event.currentTarget.contains(next)) setDragging(false)
      }}
      onDrop={onDrop}
    >
      {dragging && (
        <div
          className="fixed inset-0 z-50 bg-primary/20 backdrop-blur-sm flex items-center justify-center pointer-events-none"
          data-pdf-drop
        >
          <div className="bg-card border-2 border-dashed border-primary rounded-3xl p-10 text-center shadow-2xl">
            <Upload className="w-10 h-10 mx-auto text-primary mb-3" />
            <p className="text-lg font-black">
              <T>Pustite PDF faktúry sem</T>
            </p>
          </div>
        </div>
      )}

      <PageHeader
        title={tt('Faktúry')}
        description={tt('Prijaté faktúry od dodávateľov')}
        icon={<Receipt className="w-5 h-5" />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PdfButton busy={parsePdf.isPending} onFiles={uploadPdf} />
            <button
              type="button"
              onClick={() => {
                setPrefill(null)
                setCreating(true)
              }}
              className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <Plus className="w-4 h-4" />
              <T>Nová faktúra</T>
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-invoice-totals>
        <StatCard
          label={tt('Faktúr')}
          value={String(books.totalCount)}
          icon={FileText}
          color="blue"
        />
        <StatCard
          label={tt('Nezaplatené')}
          value={formatCents(books.unpaidCents)}
          sub={`${books.unpaidCount} ${tt('faktúr')}`}
          icon={BadgeEuro}
          color={books.unpaidCents > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('Po termíne')}
          value={String(books.overdueCount)}
          sub={books.overdueCount > 0 ? formatCents(books.overdueCents) : undefined}
          icon={Clock}
          color={books.overdueCount > 0 ? 'red' : 'emerald'}
        />
        {/* Not on the stats endpoint, and rightly so: *the header does not add up* is a judgement
            this app makes about a row it is holding, not a figure the server keeps. It stays over
            what is on screen, and the line below says how much that is. */}
        <StatCard
          label={tt('Súčty nesúhlasia')}
          value={String(totals.suspectCount)}
          icon={AlertCircle}
          color={totals.suspectCount > 0 ? 'amber' : 'emerald'}
        />
      </div>

      {/* Two numbers meaning different things have to say which is which, or they read as
          disagreeing. Nothing at all when nothing is filtered. */}
      {filteredNote(shown.length, books, filtered) !== '' && (
        <p className="text-[11px] text-muted-foreground" data-invoice-filtered>
          {filteredNote(shown.length, books, filtered)}
        </p>
      )}

      {/* FB-092 - said before anybody ticks twenty invoices, not after. */}
      {sender !== null && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-sender-problem>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            {sender === 'missing' ? (
              <T>
                SEPA dávku sa nedá vytvoriť - v Banke nie je nastavený IBAN odosielateľa. Bez neho
                server odmietne aj tú najmenšiu dávku.
              </T>
            ) : (
              <T>IBAN odosielateľa v Banke nemá platnú kontrolnú číslicu - banka dávku odmietne.</T>
            )}
          </span>
        </p>
      )}

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať číslo, dodávateľa, VS...'),
        }}
        filters={
          <>
            <FilterSelect
              value={status}
              label={tt('Stav faktúry')}
              onChange={setStatus}
              options={[
                { value: 'all', label: tt('Všetky') },
                { value: 'unpaid', label: tt('Nezaplatené') },
                { value: 'draft', label: tt('Koncepty') },
                { value: 'confirmed', label: tt('Potvrdené') },
                { value: 'paid', label: tt('Zaplatené') },
              ]}
            />
            <FilterSelect
              value={supplierFilter}
              label={tt('Dodávateľ')}
              onChange={setSupplierFilter}
              options={[
                { value: 'all', label: tt('Všetci dodávatelia') },
                ...suppliers.map((supplier) => ({
                  value: String(supplier.id ?? ''),
                  label: supplier.name ?? '',
                })),
              ]}
            />
            <button
              type="button"
              onClick={() => setSelected(new Set(payableIds(invoices)))}
              className="px-3 py-2 rounded-xl border border-border text-xs font-semibold hover:bg-muted/50 transition-colors"
            >
              <T>Označiť platiteľné</T>
            </button>
          </>
        }
      />

      {plan.included.length + plan.skipped.length > 0 && (
        <div
          className="rounded-2xl border-2 border-primary/40 bg-primary/5 px-4 py-3 space-y-2"
          data-sepa-bar
        >
          <div className="flex flex-wrap items-center gap-3">
            <FileDown className="w-5 h-5 text-primary shrink-0" />
            <p className="text-sm font-bold flex-1">
              {plan.included.length} <T>faktúr do dávky, spolu</T>{' '}
              <span className="tabular-nums">{formatCents(plan.grossCents)}</span>
            </p>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-primary/10"
            >
              <T>Zrušiť výber</T>
            </button>
            <button
              type="button"
              onClick={() => setBatching(true)}
              disabled={plan.included.length === 0 || sender !== null || sepaBatch.isPending}
              className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {sepaBatch.isPending ? <Spinner className="w-4 h-4" /> : <FileDown className="w-4 h-4" />}
              <T>Stiahnuť SEPA XML</T>
            </button>
          </div>
          {plan.skipped.length > 0 && (
            <p className="text-xs text-muted-foreground" data-sepa-skipped>
              {/* Legacy reports the skip count in a header, after the file has downloaded. */}
              <T>Vynechané:</T>{' '}
              {plan.skipped
                .map((entry) => `${entry.invoice.invoiceNumber ?? ''} (${skipReasonText(entry.reason)})`)
                .join(' · ')}
            </p>
          )}
          {plan.suspectIbans.length > 0 && (
            <p className="text-xs text-red-500" data-sepa-suspect-iban>
              <T>IBAN neprejde kontrolnou číslicou:</T>{' '}
              {plan.suspectIbans.map((invoice) => invoice.invoiceNumber ?? '').join(', ')} -{' '}
              <T>banka takú platbu odmietne až po nahraní dávky.</T>
            </p>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div data-invoices>
          <DataTable<InvoiceRow>
            columns={columns}
            data={shown}
            rowKey={(invoice) => invoice.id ?? 0}
            pageSize={25}
            compact
            emptyMessage={tt('Žiadne faktúry')}
            emptyIcon={<Receipt className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {viewingId !== null && (
        <DetailDialog
          invoiceId={viewingId}
          thresholdCents={thresholdCents}
          onClose={() => setViewingId(null)}
        />
      )}
      {editingId !== null && (
        <FormDialog
          invoiceId={editingId}
          suppliers={suppliers}
          onClose={() => setEditingId(null)}
        />
      )}
      {creating && (
        <FormDialog
          invoiceId={null}
          suppliers={suppliers}
          prefill={prefill}
          onClose={() => {
            setCreating(false)
            setPrefill(null)
          }}
        />
      )}

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming?.id !== undefined && confirm.mutate({ invoiceId: confirming.id })}
        title={`${tt('Potvrdiť faktúru')} ${confirming?.invoiceNumber ?? ''}?`}
        // FB-091. It said the goods would NOT be received, which was true: the confirm changed the
        // status and nothing else. It creates the receipt, a movement per mapped line and refreshes
        // the cost price now, so the dialog says what will happen and names what will not.
        // The count of what will be stocked lives on the detail panel, which has the lines; a list
        // row does not carry them, and guessing from the row would be worse than not saying.
        description={tt(
          'Potvrdenie zmení stav faktúry na „Potvrdená" a prijme jej priradené riadky na sklad - vytvorí príjemku, pohyb zásob a obnoví nákupnú cenu. Riadok bez skladovej položky sa neprijme.',
        )}
        confirmLabel={tt('Potvrdiť')}
        loading={confirm.isPending}
      />

      {/* §9.76 / BACKEND.md §10A.20. The wording is the safety: it states the claim being made and
          what it costs if it is wrong, rather than asking whether somebody is sure. */}
      <ConfirmDialog
        open={withdrawing !== null}
        onClose={() => {
          setWithdrawing(null)
          setWithdrawReason('')
        }}
        onConfirm={() =>
          withdrawing?.id !== undefined &&
          withdraw.mutate({
            invoiceId: withdrawing.id,
            data: { reason: withdrawReason.trim() },
          })
        }
        variant="danger"
        title={`${tt('Stiahnuť faktúru')} ${withdrawing?.invoiceNumber ?? ''} ${tt('z dávky')}?`}
        description={withdrawing === null ? undefined : withdrawWarning(withdrawing)}
        confirmLabel={tt('Stiahnuť z dávky')}
        loading={withdraw.isPending}
        confirmDisabled={!withdrawReasonAccepted(withdrawReason)}
      >
        <label className="block text-xs font-semibold text-muted-foreground">
          <T>Dôvod</T>
          <input
            value={withdrawReason}
            onChange={(event) => setWithdrawReason(event.target.value)}
            maxLength={500}
            autoFocus
            placeholder={tt('Napr. dávka sa nikdy nenahrala do banky')}
            aria-label={tt('Dôvod stiahnutia z dávky')}
            data-withdraw-reason
            className={`${INPUT} mt-1`}
          />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={approving !== null}
        onClose={() => setApproving(null)}
        onConfirm={() => approving?.id !== undefined && approve.mutate({ invoiceId: approving.id })}
        title={`${tt('Schváliť faktúru')} ${approving?.invoiceNumber ?? ''} ${
          approving ? `(${formatCents(headerTotals(approving).grossCents)})` : ''
        }?`}
        description={tt(
          'Suma prekračuje nastavený limit. Schválením potvrdzujete, že faktúra môže byť zaplatená.',
        )}
        confirmLabel={tt('Schváliť')}
        loading={approve.isPending}
      />

      <ConfirmDialog
        open={marking !== null}
        onClose={() => setMarking(null)}
        onConfirm={() => marking?.id !== undefined && markPaid.mutate({ invoiceId: marking.id })}
        title={`${tt('Označiť')} ${marking?.invoiceNumber ?? ''} ${tt('ako zaplatenú?')}`}
        description={tt('Toto len zapíše, že platba odišla - samo sa nič neposiela.')}
        confirmLabel={tt('Označiť')}
        loading={markPaid.isPending}
      />

      <ConfirmDialog
        open={unmarking !== null}
        onClose={() => setUnmarking(null)}
        onConfirm={() => unmarking?.id !== undefined && markUnpaid.mutate({ invoiceId: unmarking.id })}
        title={`${tt('Vrátiť')} ${unmarking?.invoiceNumber ?? ''} ${tt('na nezaplatenú?')}`}
        // The server puts it back to `confirmed`, not `draft`: it was checked before it was
        // paid, and a mis-click should not cost that.
        description={tt('Faktúra sa vráti do stavu „Potvrdená", nie do konceptu.')}
        confirmLabel={tt('Vrátiť')}
        loading={markUnpaid.isPending}
      />

      <ConfirmDialog
        open={batching}
        onClose={() => setBatching(false)}
        onConfirm={() =>
          sepaBatch.mutate({
            data: {
              invoiceIds: plan.included.map((invoice) => invoice.id as number),
              executionDate: defaultExecutionDate(now),
            },
          })
        }
        title={`${tt('Vytvoriť SEPA dávku na')} ${formatCents(plan.grossCents)}?`}
        description={tt(
          'Stiahne sa XML súbor, ktorý nahráte do internet bankingu a tam ho schválite - týmto krokom sa nič neplatí. Každá faktúra v dávke dostane záznam „zaradená do dávky", aby sa nezaplatila dvakrát.',
        )}
        confirmLabel={tt('Vytvoriť súbor')}
        loading={sepaBatch.isPending}
      />
    </div>
  )
}

// ─── the detail ──────────────────────────────────────────────────────────────

function DetailDialog({
  invoiceId,
  thresholdCents,
  onClose,
}: {
  invoiceId: number
  /** `null` when no approval gate is configured, which is not a gate at zero — FB-135. */
  thresholdCents: number | null
  onClose: () => void
}) {
  const { data, isLoading } = useGetInvoicesInvoiceId(invoiceId)
  const { data: attemptData } = useGetInvoicesInvoiceIdPaymentAttempts(invoiceId)
  const invoice = (data ?? {}) as InvoiceDetail
  const lines = invoice.lines ?? []
  const queryClient = useQueryClient()
  const [attachError, setAttachError] = useState<string | null>(null)

  /**
   * Multipart, so it goes through `customFetch` directly — the generated hook would `JSON.stringify`
   * the file. The wrapper still carries the auth and tenant headers (§6).
   */
  const upload = async (kind: 'pdf' | 'photo', file: File) => {
    const problem = fileProblem(kind, { type: file.type, name: file.name })
    if (problem !== null) {
      setAttachError(problem)
      return
    }
    setAttachError(null)
    try {
      const body = new FormData()
      body.append(fieldNameFor(kind), file)
      await customFetch<unknown>(endpointFor(kind, invoiceId), { method: 'POST', body })
      void queryClient.invalidateQueries()
    } catch (caught) {
      setAttachError(messageOf(caught) ?? tt('Prílohu sa nepodarilo nahrať.'))
    }
  }
  const delta = headerDelta(invoice)
  const header = headerTotals(invoice)
  const sum = lineSum(lines)
  const bands = vatBands(lines)
  const impact = stockImpact(lines)
  const attempts = byMostRecent((attemptData ?? []))

  return (
    <Modal title={`${tt('Faktúra')} ${invoice.invoiceNumber ?? ''}`} onClose={onClose} wide>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <Detail label={tt('Dodávateľ')} value={invoice.supplierName} />
            <Detail label={tt('Variabilný symbol')} value={invoice.variableSymbol} />
            <Detail label={tt('Konštantný symbol')} value={invoice.constantSymbol} />
            <Detail label={tt('Vystavená')} value={invoice.issueDate} />
            <Detail label={tt('Splatná')} value={invoice.dueDate} />
            <Detail label={tt('Dodanie')} value={invoice.deliveryDate} />
            <Detail label="IBAN" value={invoice.iban} mono />
            <Detail label={tt('Forma úhrady')} value={invoice.paymentForm} />
            <Detail label={tt('Stav')} value={statusLabel(invoice.status)} />
          </div>

          {invoice.iban && !payeeIbanValid(invoice) && (
            <Warning tone="red" testId="detail-iban-problem">
              <T>IBAN dodávateľa nemá platnú kontrolnú číslicu - banka platbu odmietne.</T>
            </Warning>
          )}

          {!headerAddsUp(invoice) && (
            <Warning tone="red" testId="detail-header-adds-up">
              <span>
                <T>Hlavička nesedí sama so sebou:</T> {formatCents(header.netCents)} +{' '}
                {formatCents(header.vatCents)} = {formatCents(header.netCents + header.vatCents)},{' '}
                <T>ale na úhradu je</T> <strong>{formatCents(header.grossCents)}</strong>.{' '}
                <T>Platí sa suma na úhradu.</T>
              </span>
            </Warning>
          )}

          {/* FB-090 - named, with the reason when there is one. */}
          {hasLines(invoice) && !delta.agrees && (
            <Warning tone="amber" testId="detail-header-delta">
              <span>
                <T>Hlavička dodávateľa hovorí</T> <strong>{formatCents(header.grossCents)}</strong>,{' '}
                <T>riadky dávajú</T> <strong>{formatCents(sum.grossCents)}</strong>{' '}
                (<T>rozdiel</T> {formatCents(Math.abs(delta.grossCents))}).{' '}
                {wholeBaseExplainsDelta(invoice) ? (
                  <T>
                    Dodávateľ počíta DPH z celého základu, nie po riadkoch - obe sumy sú správne, len
                    inak zaokrúhlené.
                  </T>
                ) : (
                  <T>Skontrolujte, či sú zapísané všetky riadky. Uložená hlavička sa nemení sama.</T>
                )}
              </span>
            </Warning>
          )}

          {/* The paper behind the invoice. One key each, and uploading replaces - see `attachments.ts`. */}
          <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-attachments>
            <h4 className="text-sm font-bold">
              <T>Priložené dokumenty</T>
            </h4>
            {!hasAttachment(invoice.pdfBlobKey) && !hasAttachment(invoice.attachmentBlobKey) ? (
              <p className="text-[11px] text-muted-foreground" data-attachments-empty>
                {emptyNote()}
              </p>
            ) : (
              <ul className="space-y-1 text-xs">
                {hasAttachment(invoice.pdfBlobKey) && (
                  <li>
                    <a
                      href={attachmentUrl(invoice.pdfBlobKey) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      data-attachment="pdf"
                      className="text-primary hover:underline"
                    >
                      <T>PDF dodávateľa</T>
                    </a>
                  </li>
                )}
                {hasAttachment(invoice.attachmentBlobKey) && (
                  <li>
                    <a
                      href={attachmentUrl(invoice.attachmentBlobKey) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      data-attachment="photo"
                      className="text-primary hover:underline"
                    >
                      <T>Fotka dokladu</T>
                    </a>
                  </li>
                )}
              </ul>
            )}

            {(['pdf', 'photo'] as const).map((kind) => {
              const existing = kind === 'pdf' ? invoice.pdfBlobKey : invoice.attachmentBlobKey
              const warning = replaceWarning(kind, existing ?? null)
              return (
                <div key={kind} className="space-y-1">
                  <label className="flex items-center gap-2 text-xs">
                    <Paperclip className="w-3.5 h-3.5 text-primary" />
                    {kind === 'pdf' ? <T>Nahrať PDF</T> : <T>Nahrať fotku</T>}
                    <input
                      type="file"
                      accept={kind === 'pdf' ? 'application/pdf' : 'image/*'}
                      data-attach-input={kind}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file !== undefined) void upload(kind, file)
                      }}
                      className="text-[11px]"
                    />
                  </label>
                  {warning !== '' && (
                    <p className="text-[11px] text-amber-500" data-attach-replace={kind}>
                      {warning}
                    </p>
                  )}
                </div>
              )
            })}

            {attachError !== null && (
              <p className="text-[11px] text-destructive" data-attach-error>
                {attachError}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground" data-attach-no-delete>
              {noDeleteNote()}
            </p>
          </div>

          {lines.length === 0 && (
            <Warning tone="amber" testId="detail-no-lines">
              <T>Faktúra nemá zapísané položky - je tu len hlavička.</T>
            </Warning>
          )}

          <div className="overflow-x-auto" data-invoice-lines>
            <table className="w-full text-xs">
              <thead className="text-muted-foreground uppercase tracking-wider">
                <tr>
                  <th className="text-left py-1.5">{tt('Kód')}</th>
                  <th className="text-left py-1.5">{tt('Názov')}</th>
                  <th className="text-right py-1.5">{tt('Množstvo')}</th>
                  <th className="text-right py-1.5">{tt('Cena/j.')}</th>
                  <th className="text-right py-1.5">DPH</th>
                  <th className="text-right py-1.5">{tt('Spolu')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const totals = statedLineTotals(line)
                  const drift = lineNetDriftCents(line)
                  return (
                    <tr key={line.id ?? index} className="border-t border-border">
                      <td className="py-1.5 font-mono text-[11px]">{line.supplierItemCode ?? ''}</td>
                      <td className="py-1.5">
                        {line.itemName}
                        {line.countryOfOrigin && (
                          <span className="ml-1.5 text-[10px] text-muted-foreground">
                            {line.countryOfOrigin}
                          </span>
                        )}
                        {(line.stockItemId ?? null) === null && (
                          <span className="ml-1.5 text-[10px] text-amber-500" data-line-unmapped>
                            <T>bez skladovej položky</T>
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {formatQty(line.qty)} {line.unit}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {formatRate(line.unitPriceAfterDiscount)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{rateOf(line.vatRate)} %</td>
                      <td className="py-1.5 text-right tabular-nums font-bold">
                        {formatCents(totals.grossCents)}
                        {drift !== 0 && (
                          <span
                            className="block text-[10px] font-normal text-muted-foreground"
                            data-line-drift
                            title={tt('Množstvo × cena za jednotku dáva inú sumu - cena na papieri je zaokrúhlená')}
                          >
                            {tt('množstvo × cena')} {formatCents(totals.netCents - drift)}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* The recapitulation, with both roundings where they differ. */}
          {bands.length > 0 && (
            <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5" data-vat-bands>
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                <T>Rekapitulácia DPH</T>
                {isMixedRate(lines) && (
                  <span className="ml-1.5 font-normal normal-case">
                    ({bands.length} {tt('sadzby')})
                  </span>
                )}
              </p>
              {bands.map((band) => (
                <div key={band.rate} className="flex justify-between text-xs tabular-nums">
                  <span className="text-muted-foreground">
                    {band.rate} % - <T>základ</T> {formatCents(band.netCents)}
                  </span>
                  <span>
                    {formatCents(band.vatCents)}
                    {band.wholeBaseVatCents !== band.vatCents && (
                      <span className="ml-1.5 text-[10px] text-muted-foreground">
                        {tt('z celého základu')} {formatCents(band.wholeBaseVatCents)}
                      </span>
                    )}
                  </span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-bold tabular-nums pt-1 border-t border-border">
                <span>
                  <T>Na úhradu podľa dodávateľa</T>
                </span>
                <span className="text-primary">{formatCents(header.grossCents)}</span>
              </div>
            </div>
          )}

          {/* FB-091 - what the confirm will do, in numbers. */}
          {lines.length > 0 && (
            <p className="text-xs text-muted-foreground" data-stock-impact>
              <T>Skladom:</T> {impact.mappedLines}/{impact.totalLines}{' '}
              <T>riadkov má priradenú skladovú položku.</T> {stockImpactNote(impact)}{' '}
              {impact.unmappedLines > 0 && (
                <T>
                  Kódy dodávateľa sa dajú priradiť v úprave faktúry, tlačidlom „Priradiť podľa kódov
                  dodávateľa".
                </T>
              )}
            </p>
          )}

          {needsApproval(invoice, thresholdCents) && (
            <Warning tone={invoice.approvedAt ? 'emerald' : 'amber'} testId="detail-approval">
              {invoice.approvedAt ? (
                <span>
                  <T>Schválené</T> {formatStamp(invoice.approvedAt)}
                  {invoice.approvedBy ? ` · #${invoice.approvedBy}` : ''}
                </span>
              ) : (
                <T>
                  Suma prekračuje limit - pred zaplatením ju musí schváliť hlavný administrátor.
                </T>
              )}
            </Warning>
          )}

          {attempts.length > 0 && (
            <div className="space-y-1" data-payment-attempts>
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                <T>Pokusy o platbu</T>
              </p>
              {attempts.map((attempt, index) => (
                <p key={index} className="text-xs text-muted-foreground">
                  {formatStamp(attempt.attemptAt)} - {attemptLabel(attempt)}
                </p>
              ))}
            </div>
          )}

          {invoice.notes && <p className="text-xs text-muted-foreground">{invoice.notes}</p>}
        </>
      )}
    </Modal>
  )
}

// ─── the form ────────────────────────────────────────────────────────────────

/**
 * The edit path waits for the invoice before the form exists.
 *
 * Seeding a mounted form from a query that arrives later means either a ref or an effect,
 * and both leave a window in which somebody can type into fields that are about to be
 * overwritten. Holding the form back until the data is in hand lets `useState` do the
 * seeding, which is one fewer way to lose an edit.
 */
function FormDialog({
  invoiceId,
  suppliers,
  prefill,
  onClose,
}: {
  invoiceId: number | null
  suppliers: SupplierOption[]
  prefill?: { header: HeaderForm; lines: LineForm[]; parsed: ParsedInvoice } | null
  onClose: () => void
}) {
  const { data: existing, isLoading } = useGetInvoicesInvoiceId(invoiceId ?? 0, {
    query: { enabled: invoiceId !== null },
  })

  if (invoiceId !== null && (isLoading || !existing)) {
    return (
      <Modal title={tt('Upraviť faktúru')} onClose={onClose} wide>
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      </Modal>
    )
  }

  const detail = (existing ?? {}) as InvoiceDetail
  const seed =
    invoiceId === null
      ? (prefill ?? { header: headerFormOf({}), lines: [emptyLineForm('19')], parsed: null })
      : {
          header: headerFormOf(detail),
          lines: (detail.lines ?? []).length > 0 ? (detail.lines ?? []).map(lineFormOf) : [emptyLineForm('19')],
          parsed: null,
        }

  return (
    <InvoiceForm
      key={invoiceId ?? 'new'}
      invoiceId={invoiceId}
      suppliers={suppliers}
      seed={seed}
      status={detail.status ?? 'draft'}
      onClose={onClose}
    />
  )
}

function InvoiceForm({
  invoiceId,
  suppliers,
  seed,
  status,
  onClose,
}: {
  invoiceId: number | null
  suppliers: SupplierOption[]
  seed: { header: HeaderForm; lines: LineForm[]; parsed: ParsedInvoice | null }
  /** §10A.22 — what the invoice already is, which decides whether its amounts may still move. */
  status: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: stockData } = useGetStockItems({ active: 'true' })
  const stockItems = useMemo<StockItemOption[]>(() => (stockData ?? []), [stockData])

  const [header, setHeader] = useState<HeaderForm>(seed.header)
  // §10A.22 — a draft may have its lines and totals changed; anything further along may not.
  const amounts = amountsEditable({ status })
  const [lines, setLines] = useState<LineForm[]>(seed.lines)
  const [resolving, setResolving] = useState(false)
  const parsed = seed.parsed
  const unresolved = unresolvedCodes(lines)

  /**
   * Ask the server what each unresolved supplier code maps to, and fill it in.
   *
   * This is step one of legacy's confirm, which AGOS's confirm no longer does (FB-091). It
   * only reads — `GET /suppliers/{id}/resolve-code/{code}`, one call per distinct code, a
   * miss answering 404 and leaving the line alone. Nothing is stocked and nothing is
   * learned; what it recovers is the routing, so a person is not picking mozzarella out of a
   * dropdown for a code the system already knows.
   */
  const resolveCodes = async () => {
    const supplierId = Number(header.supplierId)
    if (!Number.isFinite(supplierId) || supplierId <= 0) return
    setResolving(true)
    const found = new Map<string, number>()
    await Promise.all(
      unresolved.map(async (code) => {
        try {
          const mapping = await getSuppliersSupplierIdResolveCodeCode(supplierId, code)
          const stockItemId = (mapping as { stockItemId?: number }).stockItemId
          if (typeof stockItemId === 'number') found.set(code, stockItemId)
        } catch {
          // Not mapped. The dropdown is still there.
        }
      }),
    )
    setResolving(false)
    if (found.size === 0) {
      toast({ title: tt('Žiadny z kódov nemá priradenú skladovú položku.') })
      return
    }
    setLines((current) =>
      current.map((line) => {
        const stockItemId = found.get(line.supplierItemCode.trim())
        if (stockItemId === undefined || line.stockItemId.trim() !== '') return line
        return { ...line, stockItemId: String(stockItemId) }
      }),
    )
    toast({
      title: `${found.size} ${tt('z')} ${unresolved.length} ${tt('kódov priradených ku skladu')}`,
    })
  }

  const problems = formProblems(header, lines)
  const delta = formDelta(header, lines)
  const reviews = reviewCount(lines)
  const warnings = parsed ? parseWarnings(parsed) : []

  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Faktúru sa nepodarilo uložiť.'),
      variant: 'destructive',
    })
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: getGetInvoicesQueryKey() })
    onClose()
  }

  const create = usePostInvoices({ mutation: { onSuccess: done, onError } })
  const patch = usePatchInvoicesInvoiceId({ mutation: { onError } })
  const replaceLines = usePutInvoicesInvoiceIdLines({ mutation: { onSuccess: done, onError } })

  const setLine = (index: number, patchLine: Partial<LineForm>) =>
    setLines((current) =>
      current.map((line, position) => (position === index ? withEdit(line, patchLine) : line)),
    )

  /** A supplier's stored defaults, filled in only where the form is still empty. */
  const pickSupplier = (id: string) => {
    const supplier = suppliers.find((candidate) => String(candidate.id ?? '') === id)
    setHeader((current) => ({
      ...current,
      supplierId: id,
      iban: current.iban || (supplier?.iban ?? ''),
      paymentForm: current.paymentForm || (supplier?.defaultPaymentForm ?? ''),
      constantSymbol: current.constantSymbol || (supplier?.defaultConstantSymbol ?? ''),
    }))
  }

  const save = () => {
    const body = { ...headerPayload(header), lines: linePayloads(lines) }
    if (invoiceId === null) {
      create.mutate({ data: body as never })
      return
    }
    // The header and the lines are two endpoints; the lines land last so a failure leaves
    // the ones that were there. `InvoiceService.replaceLines` is one transaction, which is
    // the repair that module exists for.
    // §10A.22. A confirmed invoice's lines were posted to stock, so the server refuses to replace
    // them and this must not ask: the header alone is what is still correctable.
    if (!amounts) {
      patch.mutate({ invoiceId, data: headerPayload(header) }, { onSuccess: done })
      return
    }
    patch.mutate(
      { invoiceId, data: headerPayload(header) },
      { onSuccess: () => replaceLines.mutate({ invoiceId, data: { lines: linePayloads(lines) } as never }) },
    )
  }

  const busy = create.isPending || patch.isPending || replaceLines.isPending

  return (
    <Modal
      title={invoiceId === null ? tt('Nová faktúra') : `${tt('Upraviť faktúru')} ${header.invoiceNumber}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || !canSave(problems)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {busy ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť faktúru</T>}
          </button>
        </>
      }
    >
      <>
          {parsed && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 space-y-1" data-parse-summary>
              <p className="text-xs font-bold">
                <T>PDF prečítané</T> - {parseModeLabel(parsed.parseMode)},{' '}
                {parsed.lines?.length ?? 0} <T>riadkov</T>
                {(parsed.resolvedLineCount ?? 0) > 0 && (
                  <>
                    {', '}
                    {parsed.resolvedLineCount} <T>priradených ku skladu</T>
                  </>
                )}
                .
              </p>
              <p className="text-xs text-muted-foreground">
                <T>
                  Celková suma je tá z papiera. Porovnajte ju so súčtom riadkov nižšie - neprepisuje sa
                  sama.
                </T>
              </p>
              {warnings.map((warning) => (
                <p key={warning} className="text-xs text-amber-500" data-parse-warning>
                  {warning}
                </p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={tt('Dodávateľ')}>
              <select
                value={header.supplierId}
                onChange={(event) => pickSupplier(event.target.value)}
                aria-label={tt('Dodávateľ')}
                className={INPUT}
              >
                <option value="">{tt('- vybrať -')}</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={String(supplier.id ?? '')}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={tt('Číslo faktúry')}>
              <input
                value={header.invoiceNumber}
                onChange={(event) => setHeader({ ...header, invoiceNumber: event.target.value })}
                aria-label={tt('Číslo faktúry')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Variabilný symbol')}>
              <input
                value={header.variableSymbol}
                onChange={(event) => setHeader({ ...header, variableSymbol: event.target.value })}
                aria-label={tt('Variabilný symbol')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Konštantný symbol')}>
              <input
                value={header.constantSymbol}
                onChange={(event) => setHeader({ ...header, constantSymbol: event.target.value })}
                aria-label={tt('Konštantný symbol')}
                className={INPUT}
              />
            </Field>
            <Field label="IBAN">
              <input
                value={header.iban}
                onChange={(event) => setHeader({ ...header, iban: event.target.value })}
                aria-label="IBAN"
                className={INPUT}
              />
            </Field>
            <Field label={tt('Forma úhrady')}>
              <input
                value={header.paymentForm}
                onChange={(event) => setHeader({ ...header, paymentForm: event.target.value })}
                aria-label={tt('Forma úhrady')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Vystavená')}>
              <input
                type="date"
                value={header.issueDate}
                onChange={(event) => setHeader({ ...header, issueDate: event.target.value })}
                aria-label={tt('Vystavená')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Splatná')}>
              <input
                type="date"
                value={header.dueDate}
                onChange={(event) => setHeader({ ...header, dueDate: event.target.value })}
                aria-label={tt('Splatná')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Dodanie')}>
              <input
                type="date"
                value={header.deliveryDate}
                onChange={(event) => setHeader({ ...header, deliveryDate: event.target.value })}
                aria-label={tt('Dodanie')}
                className={INPUT}
              />
            </Field>
          </div>

          {/* FB-090 - the supplier's totals are fields, not a computed footer. */}
          <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-2" data-header-totals>
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              <T>Sumy z faktúry dodávateľa</T>
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Field label={tt('Základ')}>
                <input
                  value={header.totalNet}
                  onChange={(event) => setHeader({ ...header, totalNet: event.target.value })}
                  aria-label={tt('Základ z faktúry')}
                  readOnly={!amounts}
                  className={INPUT}
                />
              </Field>
              <Field label="DPH">
                <input
                  value={header.totalVat}
                  onChange={(event) => setHeader({ ...header, totalVat: event.target.value })}
                  aria-label={tt('DPH z faktúry')}
                  readOnly={!amounts}
                  className={INPUT}
                />
              </Field>
              <Field label={tt('Na úhradu')}>
                <input
                  value={header.totalGross}
                  onChange={(event) => setHeader({ ...header, totalGross: event.target.value })}
                  aria-label={tt('Na úhradu z faktúry')}
                  readOnly={!amounts}
                  className={INPUT}
                />
              </Field>
            </div>
            {!hasFormLines(lines) ? null : delta.agrees ? (
              <p className="text-xs text-emerald-500" data-form-delta-ok>
                <T>Súhlasí so súčtom riadkov.</T>
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-2" data-form-delta>
                <p className="text-xs text-amber-500 flex-1">
                  <T>Riadky dávajú</T>{' '}
                  <strong>{formatCents(lineSum(lines.map(lineOf)).grossCents)}</strong>,{' '}
                  <T>hlavička</T> <strong>{formatCents(parseCentsOf(header.totalGross))}</strong>.{' '}
                  <T>Uloží sa suma z hlavičky, nie súčet riadkov.</T>
                </p>
                <button
                  type="button"
                  onClick={() => setHeader({ ...header, ...totalsFromLines(lines) })}
                  className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
                >
                  <T>Použiť súčet riadkov</T>
                </button>
              </div>
            )}
          </div>

          {/* §10A.22. Said once, above the fields it explains - the server refuses these and a form
              that only failed on save would teach nothing. */}
          {!amounts && (
            <p className="text-xs text-amber-500" data-amounts-frozen>
              <T>
                Sumy a položky potvrdenej faktúry sa už nedajú meniť - boli naskladnené. Číslo,
                symboly a dátumy opraviť môžete.
              </T>
            </p>
          )}

          <div className="space-y-2" data-form-lines>
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex-1">
                <T>Položky faktúry</T>
              </p>
              {reviews > 0 && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500"
                  data-review-count
                >
                  {reviews}/{lines.length} <T>na kontrolu</T>
                </span>
              )}
              {unresolved.length > 0 && header.supplierId !== '' && (
                <button
                  type="button"
                  onClick={() => void resolveCodes()}
                  disabled={resolving}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted/50 disabled:opacity-50"
                  data-resolve-codes
                >
                  {resolving ? <Spinner className="w-3 h-3" /> : null}
                  <T>Priradiť podľa kódov dodávateľa</T> ({unresolved.length})
                </button>
              )}
            </div>
            {lines.map((line, index) => (
              <div
                key={index}
                className={cn(
                  'grid grid-cols-12 gap-2 items-end rounded-lg p-1',
                  needsReview(line) && 'bg-amber-500/5 ring-1 ring-amber-500/30',
                )}
                data-line-row
              >
                <div className="col-span-4 sm:col-span-2">
                  <input
                    value={line.supplierItemCode}
                    onChange={(event) => setLine(index, { supplierItemCode: event.target.value })}
                    aria-label={`${tt('Kód')} ${index + 1}`}
                    placeholder={tt('kód')}
                    className={cn(INPUT, 'font-mono text-xs')}
                  />
                </div>
                <div className="col-span-8 sm:col-span-3">
                  <input
                    value={line.itemName}
                    onChange={(event) => setLine(index, { itemName: event.target.value })}
                    aria-label={`${tt('Názov')} ${index + 1}`}
                    placeholder={tt('názov')}
                    className={INPUT}
                  />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <select
                    value={line.stockItemId}
                    onChange={(event) => setLine(index, { stockItemId: event.target.value })}
                    aria-label={`${tt('Skladová položka')} ${index + 1}`}
                    className={INPUT}
                  >
                    <option value="">{tt('- bez skladu -')}</option>
                    {stockItems.map((item) => (
                      <option key={item.id} value={String(item.id ?? '')}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <input
                    value={line.qty}
                    onChange={(event) => setLine(index, { qty: event.target.value })}
                    aria-label={`${tt('Množstvo')} ${index + 1}`}
                    className={INPUT}
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <input
                    value={line.unit}
                    onChange={(event) => setLine(index, { unit: event.target.value })}
                    aria-label={`${tt('Jednotka')} ${index + 1}`}
                    className={INPUT}
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <input
                    value={line.unitPrice}
                    onChange={(event) => setLine(index, { unitPrice: event.target.value })}
                    aria-label={`${tt('Cena za jednotku')} ${index + 1}`}
                    className={INPUT}
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <input
                    value={line.vatRate}
                    onChange={(event) => setLine(index, { vatRate: event.target.value })}
                    aria-label={`DPH ${index + 1}`}
                    className={INPUT}
                  />
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <span className="text-xs font-bold tabular-nums" data-line-total>
                    {formatCents(statedLineTotals(lineOf(line)).grossCents)}
                  </span>
                  {lines.length > 1 && (
                    <IconButton
                      label={`${tt('Odobrať riadok')} ${index + 1}`}
                      tone="red"
                      onClick={() =>
                        setLines((current) => current.filter((_, position) => position !== index))
                      }
                    >
                      <X className="w-3.5 h-3.5" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setLines((current) => [...current, emptyLineForm('19')])}
              className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              <Plus className="w-3.5 h-3.5" />
              <T>Pridať riadok</T>
            </button>
          </div>

          <Field label={tt('Poznámka')}>
            <input
              value={header.notes}
              onChange={(event) => setHeader({ ...header, notes: event.target.value })}
              aria-label={tt('Poznámka')}
              className={INPUT}
            />
          </Field>

          {problems.length > 0 && (
            <div className="space-y-1" data-form-problems>
              {problems.map((problem) => (
                <p
                  key={`${problem.kind}-${'index' in problem ? problem.index : ''}`}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    problem.kind === 'iban-checksum' || problem.kind === 'due-before-issue'
                      ? 'text-amber-500'
                      : 'text-red-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <ProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}
      </>
    </Modal>
  )
}

function ProblemText({ problem }: { problem: FormProblem }) {
  if (problem.kind === 'no-supplier') return <T>Dodávateľ je povinný.</T>
  if (problem.kind === 'no-number') return <T>Číslo faktúry je povinné.</T>
  if (problem.kind === 'no-lines') return <T>Faktúra musí mať aspoň jednu položku.</T>
  if (problem.kind === 'no-total') return <T>Faktúra na nulu sa nedá uložiť.</T>
  if (problem.kind === 'iban-checksum') {
    return <T>IBAN nemá platnú kontrolnú číslicu - uložiť sa dá, banka ho ale odmietne.</T>
  }
  if (problem.kind === 'due-before-issue') return <T>Splatnosť je pred dátumom vystavenia.</T>
  return (
    <span>
      <T>Riadok</T> {problem.index + 1} <T>nemá množstvo.</T>
    </span>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

function PdfButton({ busy, onFiles }: { busy: boolean; onFiles: (files: File[]) => void }) {
  return (
    <label
      className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-semibold cursor-pointer hover:bg-muted/50 transition-colors"
      title={tt('Nahrať PDF faktúru')}
    >
      {busy ? <Spinner className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
      <T>Nahrať PDF</T>
      <input
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        aria-label={tt('Nahrať PDF faktúru')}
        disabled={busy}
        onChange={(event) => {
          onFiles([...(event.target.files ?? [])])
          event.target.value = ''
        }}
      />
    </label>
  )
}

function Warning({
  tone,
  testId,
  children,
}: {
  tone: 'red' | 'amber' | 'emerald'
  testId: string
  children: ReactNode
}) {
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 text-xs leading-relaxed',
        tone === 'red' && 'text-red-500',
        tone === 'amber' && 'text-amber-500',
        tone === 'emerald' && 'text-emerald-500',
      )}
      data-warning={testId}
    >
      <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
      {children}
    </p>
  )
}

function Detail({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={cn('text-sm', mono ? 'font-mono text-xs' : 'font-semibold')}>{value || '-'}</p>
    </div>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Modal({
  title,
  onClose,
  footer,
  wide,
  children,
}: {
  title: string
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          'bg-card border border-border rounded-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl',
          wide ? 'max-w-5xl' : 'max-w-lg',
        )}
        onClick={(event) => event.stopPropagation()}
        data-invoice-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm truncate">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">{children}</div>
        {footer && <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">{footer}</div>}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}


function skipReasonText(reason: SepaSkipReason | null): string {
  if (reason === 'paid') return tt('už zaplatená')
  if (reason === 'cancelled') return tt('stornovaná')
  if (reason === 'no-iban') return tt('chýba IBAN')
  // The server's own wording, so the tooltip before the download and the skip list after it are
  // the same sentence rather than two descriptions of one refusal — §10A.19.
  if (reason === 'queued') return tt('už v dávke')
  return ''
}

/** A quantity, never padded to two decimals. `"3.000"` on the wire reads as `3`. */
function formatQty(qty: string | number | null | undefined): string {
  const rounded = Math.round(rateOf(qty) * 1000) / 1000
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
}

/** A unit price keeps its four decimals; only a total is money. */
function formatRate(value: string | number | null | undefined): string {
  const rounded = Math.round(rateOf(value) * 10_000) / 10_000
  return `${String(rounded).replace('.', ',')} €`
}

function formatStamp(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return '-'
  return new Date(stamp).toLocaleString('sk-SK')
}

function parseCentsOf(value: string): number {
  const parsed = Number(value.trim().replace(',', '.'))
  if (!Number.isFinite(parsed)) return 0
  return Math.round(parsed * 100)
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
